import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, ChevronRightIcon, XIcon } from "lucide-react";
import { DialogTitle } from "@/components/ui/dialog";
import type { ProfileGroup, ProfileSection } from "./profile-dialog";

export interface MobileProfileLayoutProps {
  groups: ProfileGroup[];
  mobileSection: ProfileSection | null;
  mobileSectionLabel: string | null;
  onSectionSelect: (section: ProfileSection) => void;
  onBack: () => void;
  onClose: () => void;
  onSignOut: () => void;
  children: React.ReactNode;
}

export function MobileProfileLayout({
  groups,
  mobileSection,
  mobileSectionLabel,
  onSectionSelect,
  onBack,
  onClose,
  onSignOut,
  children,
}: MobileProfileLayoutProps) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden md:hidden">
      {mobileSection === null ? (
        <>
          <div className="flex shrink-0 items-center justify-between border-b border-line px-4 py-3">
            <DialogTitle className="text-[15px] font-semibold text-ink-900">
              {t("profile-dialog.title")}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.buttons.close.label")}
              className="rounded-3 p-1 text-ink-500 hover:bg-surface-1 hover:text-ink-900"
            >
              <XIcon size={18} />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {groups.map((group) => (
              <div key={group.label} className="px-4 pt-4">
                <p className="mb-1 font-mono text-[9.5px] uppercase tracking-[.12em] text-ink-500">
                  {group.label}
                </p>
                {group.kind === "sections"
                  ? group.items.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => onSectionSelect(item.id)}
                        className="flex w-full items-center justify-between rounded-3 px-2 py-3 text-left text-[14px] text-ink-900 transition-colors hover:bg-surface-1"
                      >
                        <span>{item.label}</span>
                        <ChevronRightIcon size={16} className="text-ink-500" />
                      </button>
                    ))
                  : group.items.map((link) => (
                      <button
                        key={link.label}
                        type="button"
                        onClick={link.onClick}
                        className="flex w-full items-center gap-2 rounded-3 px-2 py-3 text-left text-[14px] text-ink-900 transition-colors hover:bg-surface-1"
                      >
                        {link.icon ? (
                          <span className="inline-flex size-5 items-center justify-center text-ink-500">
                            {link.icon}
                          </span>
                        ) : null}
                        <span className="flex-1">{link.label}</span>
                        {link.value ? (
                          <span className="text-[12px] text-ink-500">{link.value}</span>
                        ) : null}
                        <ChevronRightIcon size={16} className="text-ink-500" />
                      </button>
                    ))}
              </div>
            ))}
            <div className="mt-2 border-t border-line px-4 py-3">
              <button
                type="button"
                onClick={onSignOut}
                className="w-full rounded-3 px-2 py-3 text-left text-[14px] text-vroom-red hover:bg-vroom-red-soft"
              >
                {t("common.nav.sign-out.label")}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex shrink-0 items-center gap-2 border-b border-line px-3 py-3">
            <button
              type="button"
              onClick={onBack}
              aria-label={t("common.buttons.back.label")}
              className="rounded-3 p-1 text-ink-500 hover:bg-surface-1 hover:text-ink-900"
            >
              <ArrowLeftIcon size={18} />
            </button>
            <DialogTitle className="flex-1 text-[15px] font-semibold text-ink-900">
              {mobileSectionLabel ?? ""}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.buttons.close.label")}
              className="rounded-3 p-1 text-ink-500 hover:bg-surface-1 hover:text-ink-900"
            >
              <XIcon size={18} />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        </>
      )}
    </div>
  );
}
